import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'

export const COVER_DIRECTORY = Directory.Data
export const COVER_SUBDIR = 'covers'

export function isNative(): boolean {
  return Capacitor.isNativePlatform()
}

export function coverPath(coverKey: string): string {
  return `${COVER_SUBDIR}/${encodeURIComponent(coverKey)}.webp`
}

export function coverKeyFromFileName(fileName: string): string | null {
  if (!fileName.endsWith('.webp')) return null
  return decodeURIComponent(fileName.slice(0, -'.webp'.length))
}

async function coverFileExists(path: string): Promise<boolean> {
  try {
    await Filesystem.stat({ path, directory: COVER_DIRECTORY })
    return true
  } catch {
    return false
  }
}

export async function cacheCover(coverKey: string, remoteUrl: string): Promise<void> {
  if (!isNative() || coverKey.length === 0) return
  const path = coverPath(coverKey)
  if (await coverFileExists(path)) return
  try {
    await Filesystem.downloadFile({ url: remoteUrl, path, directory: COVER_DIRECTORY, recursive: true })
  } catch {
    // Transient/offline download failure — a later online view retries; never surface to the UI.
  }
}

// The mtime query param busts the WebView image cache when a cover file is
// rewritten IN PLACE under its unchanged key — a pack tier switch does exactly
// that, and without it the same src URL keeps serving the old tier's bytes.
export async function resolveCachedCoverSrc(coverKey: string): Promise<string | null> {
  if (!isNative() || coverKey.length === 0) return null
  const path = coverPath(coverKey)
  try {
    const stat = await Filesystem.stat({ path, directory: COVER_DIRECTORY })
    const { uri } = await Filesystem.getUri({ path, directory: COVER_DIRECTORY })
    return `${Capacitor.convertFileSrc(uri)}?v=${stat.mtime}`
  } catch {
    return null
  }
}
